import { useState } from 'react';

import {
  useFormik,
} from 'formik';
import * as yup from 'yup';
import {
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useCompanySignupMutation } from '../../../../../graphql/generated/graphql';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { CompanyRegisterGoogleForm } from '../../../../config/interfaces';
import HaveAccount from '../../../components/onbording/have-account';
import MsgVerify from '../../../components/onbording/msg-verify';
import OnBoardingLayout from '../../../../theme/templates/on-boarding';
import { useLoginState } from '../../../../hooks/loginInfo';


export default function RegisterGoogleCompanyPage() {
  const [signup] = useCompanySignupMutation();
  const loginState = useLoginState();
  const [signUp, setSignUp] = useState(false);
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');

  const initialValues: CompanyRegisterGoogleForm = {
    companyName: loginState.first_name.get() ?
      loginState.last_name.get() ?
        `${loginState.first_name.get()} ${loginState.last_name.get()}` as string
        : loginState.first_name.get() as string
      : '',
    address: '',
    email: loginState.email.get() ? loginState.email.get() as string : '',
    phone: '',
    password: '',
    confirmPassword: '',
  };
  const RegisterCompanySchema = yup.object().shape({
    companyName: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    address: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    email: yup.string()
      .email('Invalid email')
      .required('Required'),
    phone: yup.string()
      .min(6, 'Too Short!')
      .max(12, 'Too Long!')
      .required('Required'),
    password: yup.string()
      .min(8, 'Too Short!')
      .required('Required'),
    confirmPassword: yup.string()
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: RegisterCompanySchema,
    onSubmit: async (variables) => {
      setLoader(true);
      setEmail(variables.email);
      try {
        await signup({
          variables: {
            companyName: variables.companyName,
            address: variables.address,
            email: variables.email,
            phone: variables.phone.toString(),
            password: variables.password,
            confirmPassword: variables.confirmPassword,
          },
        });
        setSignUp(true);
      } catch (e) {
        setSignUp(false);
      }
      setLoader(false);
    },
  });

  return (
    <OnBoardingLayout>
      <Grid
        sx={{
          maxWidth: '90%',
          width: '500px',
          mb: 2,
          mx: 4,
          py: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        item
      >
        <Typography
          component="h1"
          variant="h3"
        >
          Last step!
        </Typography>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            mb: 5,
            mt: 2,
            textAlign: 'center'
          }}
        >
          We requiered to know three things about you to complete the full registration!
        </Typography>
        {signUp ? <MsgVerify email={email} /> : (
          <>
            <FormControl
              fullWidth
              component="form"
              onSubmit={formik.handleSubmit}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="company-name"
                label="Company Name"
                name="companyName"
                autoComplete="company-name"
                autoFocus
                value={formik.values.companyName}
                onChange={formik.handleChange}
                error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                helperText={formik.touched.companyName && formik.errors.companyName}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="address"
                label="Address"
                name="address"
                autoComplete="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                disabled
                label="Email"
                name="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="phone"
                label="Phone"
                type="number"
                id="phone"
                autoComplete="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirm-password"
                autoComplete="confirm-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {loader ? <CircularProgress color="primary" /> : 'Complete register'}
              </Button>
            </FormControl>
            <HaveAccount />
          </>
        )}
      </Grid>
    </OnBoardingLayout>
  );
}
